import React, { Suspense, lazy, useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./hooks/auth-provider";
import { useAuth } from "./hooks/useAuth";
import { MyCoachContinuityBoundary } from "./components/continuity/MyCoachContinuityProvider";
import { DemoModeProvider } from "./hooks/DemoModeProvider";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PreviewLeaderProvider } from "./preview/leaderPreviewContext";
import { LandingPage } from "./pages/LandingPage";
import { AuthPage } from "./pages/AuthPage";
import { JoinPage } from "./pages/JoinPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { SubscriptionSyncGate } from "./components/billing/SubscriptionSyncGate";
import { CheckoutStatusToastGate } from "./components/billing/CheckoutStatusToastGate";
import { IntegrationCallbackRedirectGate } from "./components/integrations/IntegrationCallbackRedirectGate";
import { capturePostHogRouteView, identifyPostHogUser, resetPostHogIdentity } from "@/utils/posthog";
import { GoogleAnalyticsConsent } from "./components/analytics/GoogleAnalyticsConsent";
import { LeadersPage } from "./pages/coach/LeadersPage";
import { LeaderDetailPage } from "./pages/coach/LeaderDetailPage";
import { CoachAIPage } from "./pages/coach/CoachAIPage";
import { CoachVeeraPanelHost } from "./components/chat/CoachVeeraPanelHost";
import { LeaderVeeraPanelHost } from "./components/chat/LeaderVeeraPanelHost";

const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
} as const;

const LeaderHubPage = lazy(() => import("./pages/LeaderHubPage").then((m) => ({ default: m.LeaderHubPage })));
const LeaderTalkWithCoachPage = lazy(() => import("./pages/LeaderTalkWithCoachPage").then((m) => ({ default: m.LeaderTalkWithCoachPage })));
const MomentsPage = lazy(() => import("./pages/MomentsPage").then((m) => ({ default: m.MomentsPage })));
const TodayPage = lazy(() => import("./pages/TodayPage").then((m) => ({ default: m.TodayPage })));
const InsightsPage = lazy(() => import("./pages/InsightsPage").then((m) => ({ default: m.InsightsPage })));
const VeeraPage = lazy(() => import("./pages/VeeraPage").then((m) => ({ default: m.VeeraPage })));
const OnboardingFlow = lazy(() => import("./pages/OnboardingFlow").then((m) => ({ default: m.OnboardingFlow })));
const OnboardingWaiting = lazy(() => import("./pages/OnboardingFlow").then((m) => ({ default: m.OnboardingWaiting })));
const NudgeScreen = lazy(() => import("./pages/NudgeScreen").then((m) => ({ default: m.NudgeScreen })));
const AssessmentFlow = lazy(() => import("./pages/AssessmentFlow").then((m) => ({ default: m.AssessmentFlow })));
const TranscriptAnalysisResultsPage = lazy(() => import("./pages/TranscriptAnalysisResultsPage").then((m) => ({ default: m.TranscriptAnalysisResultsPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const LeaderMessagesPage = lazy(() => import("./pages/LeaderMessagesPage").then((m) => ({ default: m.LeaderMessagesPage })));
const SynthesisPage = lazy(() => import("./pages/SynthesisPage").then((m) => ({ default: m.SynthesisPage })));
const TranscriptUploadPage = lazy(() => import("./pages/TranscriptUploadPage").then((m) => ({ default: m.TranscriptUploadPage })));
const PostMeetingReflectionPage = lazy(() => import("./pages/PostMeetingReflectionPage").then((m) => ({ default: m.PostMeetingReflectionPage })));
const CalendarUploadPage = lazy(() => import("./pages/CalendarUploadPage").then((m) => ({ default: m.CalendarUploadPage })));
const SummaryDeepLinkPage = lazy(() => import("./pages/SummaryDeepLinkPage").then((m) => ({ default: m.SummaryDeepLinkPage })));
const ReflectionsDuePage = lazy(() => import("./pages/ReflectionsDuePage").then((m) => ({ default: m.ReflectionsDuePage })));
const ReflectionCompanionPage = lazy(() => import("./pages/ReflectionCompanionPage").then((m) => ({ default: m.ReflectionCompanionPage })));
const ReflectPage = lazy(() => import("./pages/ReflectPage").then((m) => ({ default: m.ReflectPage })));
const HelpPage = lazy(() => import("./pages/HelpPage").then((m) => ({ default: m.HelpPage })));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage").then((m) => ({ default: m.IntegrationsPage })));
const SwarmDashboard = lazy(() => import("./pages/SwarmDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));

const CoachOnboardingFlow = lazy(() => import("./pages/coach/CoachOnboardingFlow").then((m) => ({ default: m.CoachOnboardingFlow })));
const CoachOverviewPage = lazy(() => import("./pages/coach/OverviewPage").then((m) => ({ default: m.CoachOverviewPage })));
const CoachingRecordPage = lazy(() => import("./pages/coach/CoachingRecordPage").then((m) => ({ default: m.CoachingRecordPage })));
const CoachHubPage = lazy(() => import("./pages/coach/CoachHubPage").then((m) => ({ default: m.CoachHubPage })));
const CoachAlignmentPage = lazy(() => import("./pages/coach/CoachAlignmentPage").then((m) => ({ default: m.CoachAlignmentPage })));
const CoachHubFrameworksPage = lazy(() => import("./pages/coach/CoachHubDetailPage").then((m) => ({ default: m.CoachHubLensPage })));
const CoachHubGuardrailsPage = lazy(() => import("./pages/coach/CoachHubDetailPage").then((m) => ({ default: m.CoachHubRecordPage })));
const CoachMessagesPage = lazy(() => import("./pages/coach/MessagesPage").then((m) => ({ default: m.CoachMessagesPage })));
const SynthesisReviewPage = lazy(() => import("./pages/coach/SynthesisReviewPage").then((m) => ({ default: m.SynthesisReviewPage })));
const TranscriptReviewPage = lazy(() => import("./pages/coach/TranscriptReviewPage").then((m) => ({ default: m.TranscriptReviewPage })));
const SequenceReviewPage = lazy(() => import("./pages/coach/SequenceReviewPage").then((m) => ({ default: m.SequenceReviewPage })));
const CoachReviewPage = lazy(() => import("./pages/coach/CoachReviewPage").then((m) => ({ default: m.CoachReviewPage })));
const CoachSettingsPage = lazy(() => import("./pages/coach/SettingsPage").then((m) => ({ default: m.CoachSettingsPage })));
const PhilosophyPage = lazy(() => import("./pages/coach/PhilosophyPage").then((m) => ({ default: m.PhilosophyPage })));
const MyVoicePage = lazy(() => import("./pages/coach/MyVoicePage").then((m) => ({ default: m.MyVoicePage })));
const CoachIdentityPage = lazy(() => import("./pages/coach/CoachIdentityPage").then((m) => ({ default: m.CoachIdentityPage })));
const CoachIdentityGuidedFormPage = lazy(() => import("./pages/coach/CoachIdentityGuidedFormPage").then((m) => ({ default: m.CoachIdentityGuidedFormPage })));
const CoachPostMeetingReviewPage = lazy(() => import("./pages/coach/CoachPostMeetingReviewPage").then((m) => ({ default: m.CoachPostMeetingReviewPage })));
const CoachHelpPage = lazy(() => import("./pages/coach/HelpPage").then((m) => ({ default: m.CoachHelpPage })));
const CoachAppsPage = lazy(() => import("./pages/coach/AppsPage").then((m) => ({ default: m.CoachAppsPage })));
const CoachCallsPage = lazy(() => import("./pages/coach/CallsPage").then((m) => ({ default: m.CoachCallsPage })));
const CoachReflectPage = lazy(() => import("./pages/coach/CoachReflectPage").then((m) => ({ default: m.CoachReflectPage })));
const CoachSessionEntryPage = lazy(() => import("./pages/coach/CoachSessionEntryPage").then((m) => ({ default: m.CoachSessionEntryPage })));
const ProductFeedbackPage = lazy(() => import("./pages/coach/ProductFeedbackPage").then((m) => ({ default: m.ProductFeedbackPage })));
const AutomationOperatorAdminPage = lazy(() => import("./pages/internal/AutomationOperatorAdminPage").then((m) => ({ default: m.AutomationOperatorAdminPage })));
const FrameworkPackEditorPage = lazy(() => import("./pages/internal/FrameworkPackEditorPage").then((m) => ({ default: m.FrameworkPackEditorPage })));
const QASignupHarness = lazy(() => import("./pages/QASignupHarness").then((m) => ({ default: m.QASignupHarness })));
const QAIdentityPage = lazy(() => import("./pages/QAIdentityPage").then((m) => ({ default: m.QAIdentityPage })));
const PostSignupPage = lazy(() => import("./pages/PostSignupPage").then((m) => ({ default: m.PostSignupPage })));
const PostAuthPage = lazy(() => import("./pages/PostAuthPage").then((m) => ({ default: m.PostAuthPage })));
const CalendarReauthorizationPage = lazy(() => import("./pages/CalendarReauthorizationPage").then((m) => ({ default: m.CalendarReauthorizationPage })));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage").then((m) => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage").then((m) => ({ default: m.TermsOfServicePage })));
const TrustPage = lazy(() => import("./pages/TrustPage").then((m) => ({ default: m.TrustPage })));
const ZoomIntegrationGuidePage = lazy(() => import("./pages/docs/ZoomIntegrationGuidePage").then((m) => ({ default: m.ZoomIntegrationGuidePage })));
const PricingPage = lazy(() => import("./pages/PricingPage").then((m) => ({ default: m.PricingPage })));
const PreviewPostMeetingCapturePage = lazy(() => import("./pages/preview/PreviewPostMeetingCapturePage").then((m) => ({ default: m.PreviewPostMeetingCapturePage })));
const PreviewOnboardingPage = lazy(() => import("./pages/preview/PreviewOnboardingPage").then((m) => ({ default: m.PreviewOnboardingPage })));

const queryClient = new QueryClient();

const AppRouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-2">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <span className="text-muted-foreground">Loading...</span>
    </div>
  </div>
);

const LeadershipRecordRedirect = () => {
  const location = useLocation();

  return <Navigate to={`/shared-memory${location.search}#leadership-record`} replace />;
};

const SettingsHashRedirect = () => {
  const location = useLocation();
  const targetHash = location.hash === "#leadership-lens" ? "#leadership-lens" : "#leadership-record";

  return <Navigate to={`/shared-memory${location.search}${targetHash}`} replace />;
};

const RedirectWithSearch = ({ to }: { to: string }) => {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

const RedirectReflectToSession = () => {
  const { meetingId } = useParams<{ meetingId: string }>();
  const { search, hash } = useLocation();
  return <Navigate to={`/coach/meetings/${meetingId ?? ""}/reflect${search}${hash}`} replace />;
};

const PostHogRouteTracker = () => {
  const location = useLocation();
  const { user, roles, loading } = useAuth();

  useEffect(() => {
    if (loading) return;

    if (user) {
      identifyPostHogUser(user, roles);
      return;
    }

    resetPostHogIdentity();
  }, [loading, roles, user]);

  useEffect(() => {
    if (loading) return;

    capturePostHogRouteView(location.pathname);
  }, [loading, location.pathname]);

  return null;
};

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <MyCoachContinuityBoundary>
          <SubscriptionSyncGate />
          <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={routerFuture}>
            <CheckoutStatusToastGate />
            <IntegrationCallbackRedirectGate />
            <GoogleAnalyticsConsent />
            <PostHogRouteTracker />
            <DemoModeProvider>
            <CoachVeeraPanelHost />
            <LeaderVeeraPanelHost />
            <Suspense fallback={<AppRouteFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/trust" element={<TrustPage />} />
              <Route path="/docs/zoom-integration" element={<ZoomIntegrationGuidePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/leader/signup" element={<AuthPage />} />
              <Route path="/leader/sign-up" element={<AuthPage />} />
              <Route path="/coach/signup" element={<AuthPage />} />
              <Route path="/coach/sign-up" element={<AuthPage />} />
              <Route path="/sign-in" element={<Navigate to="/auth" replace />} />
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/signin" element={<Navigate to="/auth" replace />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/invite/:token" element={<JoinPage />} />
              <Route path="/join/:token" element={<JoinPage />} />

              {/* QA Routes (dev-only, gated in component) */}
              <Route path="/qa/signup" element={<QASignupHarness />} />
              <Route path="/qa/identity" element={<QAIdentityPage />} />

              {/* Post-signup and post-auth flows */}
              <Route path="/post-signup" element={<PostSignupPage />} />
              <Route path="/post-auth" element={<PostAuthPage />} />
              <Route path="/calendar-reauthorization" element={<ProtectedRoute><CalendarReauthorizationPage /></ProtectedRoute>} />
              <Route path="/swarm" element={<SwarmDashboard />} />
              <Route path="/internal/automation-ops" element={<ProtectedRoute><AutomationOperatorAdminPage /></ProtectedRoute>} />
              <Route path="/internal/framework-packs" element={<ProtectedRoute><FrameworkPackEditorPage /></ProtectedRoute>} />

              {/* Leader Onboarding Routes */}
              <Route path="/onboarding" element={<ProtectedRoute role="leader" allowIncompleteOnboarding><OnboardingFlow /></ProtectedRoute>} />
              <Route path="/onboarding/waiting" element={<ProtectedRoute role="leader" allowIncompleteOnboarding><OnboardingWaiting /></ProtectedRoute>} />

              {/* Standalone minimal reflection capture — no app chrome, linkable from notifications */}
              <Route path="/reflect/:meetingId" element={<ProtectedRoute role="leader"><ReflectPage /></ProtectedRoute>} />

              {/* Leader Routes - New IA */}
              <Route path="/today" element={<ProtectedRoute role="leader"><TodayPage /></ProtectedRoute>} />
              <Route path="/shared-memory" element={<ProtectedRoute role="leader"><LeaderHubPage /></ProtectedRoute>} />
              <Route path="/shared-memory/:section" element={<ProtectedRoute role="leader"><LeaderHubPage /></ProtectedRoute>} />
              <Route path="/leadership-record" element={<ProtectedRoute role="leader"><LeadershipRecordRedirect /></ProtectedRoute>} />
              <Route path="/settings/leadership-record" element={<ProtectedRoute role="leader"><SettingsHashRedirect /></ProtectedRoute>} />
              <Route path="/leader-hub" element={<Navigate to="/shared-memory" replace />} />
              <Route path="/moments" element={<ProtectedRoute role="leader"><MomentsPage /></ProtectedRoute>} />
              <Route path="/journey" element={<Navigate to="/today" replace />} />
              <Route path="/leader/coach" element={<ProtectedRoute role="leader"><LeaderMessagesPage /></ProtectedRoute>} />
              <Route path="/leader/talk-with-coach" element={<ProtectedRoute role="leader"><LeaderTalkWithCoachPage /></ProtectedRoute>} />
              <Route path="/insights" element={<ProtectedRoute role="leader"><InsightsPage /></ProtectedRoute>} />
              <Route path="/veera" element={<ProtectedRoute role="leader"><VeeraPage /></ProtectedRoute>} />
              <Route path="/reflection-companion" element={<ProtectedRoute role="leader"><ReflectionCompanionPage /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute role="leader"><SettingsPage /></ProtectedRoute>} />
              <Route path="/apps" element={<ProtectedRoute role="leader"><IntegrationsPage /></ProtectedRoute>} />
              <Route path="/integrations" element={<Navigate to="/apps" replace />} />
              <Route path="/help" element={<ProtectedRoute role="leader"><HelpPage /></ProtectedRoute>} />

              {/* Legacy routes - redirect to new IA */}
              <Route path="/dashboard" element={<Navigate to="/today" replace />} />
              <Route path="/progress" element={<Navigate to="/journey" replace />} />
              <Route path="/messages" element={<Navigate to="/leader/coach" replace />} />
              <Route path="/more" element={<Navigate to="/today" replace />} />

              {/* Other Leader Routes (accessible but not in main nav) */}
              <Route path="/nudge/:id" element={<ProtectedRoute role="leader"><NudgeScreen /></ProtectedRoute>} />
              <Route path="/assess/:meetingId" element={<ProtectedRoute role="leader"><AssessmentFlow /></ProtectedRoute>} />
              <Route path="/synthesis" element={<ProtectedRoute role="leader"><SynthesisPage /></ProtectedRoute>} />
              <Route path="/calendar-upload" element={<ProtectedRoute role="leader"><CalendarUploadPage /></ProtectedRoute>} />

              {/* Summary deep-link routes */}
              <Route path="/journey/summary/:summaryId" element={<ProtectedRoute role="leader"><SummaryDeepLinkPage /></ProtectedRoute>} />
              <Route path="/journey/reflections-due" element={<ProtectedRoute role="leader"><ReflectionsDuePage /></ProtectedRoute>} />

              {/* Transcript routes */}
              <Route path="/transcripts" element={<ProtectedRoute role="leader"><TranscriptUploadPage /></ProtectedRoute>} />
              <Route path="/transcript" element={<Navigate to="/transcript-upload" replace />} />
              <Route path="/transcript-upload" element={<ProtectedRoute role="leader"><TranscriptUploadPage /></ProtectedRoute>} />
              <Route path="/transcript-analysis/:transcriptId" element={<ProtectedRoute role="leader"><TranscriptAnalysisResultsPage /></ProtectedRoute>} />
              <Route path="/post-meeting/:transcriptId" element={<ProtectedRoute role="leader"><PostMeetingReflectionPage /></ProtectedRoute>} />
              <Route path="/meetings/:meetingId" element={<ProtectedRoute role="leader"><PostMeetingReflectionPage /></ProtectedRoute>} />

              {/* Additional legacy redirects */}
              <Route path="/history" element={<Navigate to="/today" replace />} />
              <Route path="/focus" element={<Navigate to="/today" replace />} />

              {/* Coach Routes - New Primary Nav */}
              <Route path="/coach" element={<ProtectedRoute role="coach"><Navigate to="/coach/overview" replace /></ProtectedRoute>} />
              <Route path="/coach/overview" element={<ProtectedRoute role="coach"><CoachOverviewPage /></ProtectedRoute>} />
              <Route path="/coach/record" element={<ProtectedRoute role="coach"><CoachingRecordPage /></ProtectedRoute>} />
              <Route path="/coach/leaders" element={<ProtectedRoute role="coach"><LeadersPage /></ProtectedRoute>} />
              <Route path="/coach/leaders/:leaderId" element={<ProtectedRoute role="coach"><LeaderDetailPage /></ProtectedRoute>} />
              <Route path="/coach/leaders/:leaderId/record" element={<ProtectedRoute role="coach"><CoachingRecordPage /></ProtectedRoute>} />
              <Route path="/coach/leaders/:leaderId/synthesis/:behaviorId" element={<ProtectedRoute role="coach"><SynthesisReviewPage /></ProtectedRoute>} />
              <Route path="/coach/reviews" element={<ProtectedRoute role="coach"><TranscriptReviewPage /></ProtectedRoute>} />
              <Route path="/coach/hub" element={<ProtectedRoute role="coach"><CoachHubPage /></ProtectedRoute>} />
              <Route path="/coach/hub/identity" element={<ProtectedRoute role="coach"><Navigate to="/coach/identity" replace /></ProtectedRoute>} />
              <Route path="/coach/hub/philosophy" element={<ProtectedRoute role="coach"><Navigate to="/coach/identity" replace /></ProtectedRoute>} />
              <Route path="/coach/hub/frameworks" element={<ProtectedRoute role="coach"><CoachHubFrameworksPage /></ProtectedRoute>} />
              <Route path="/coach/hub/guardrails" element={<ProtectedRoute role="coach"><Navigate to="/coach/identity" replace /></ProtectedRoute>} />
              <Route path="/coach/hub/materials" element={<ProtectedRoute role="coach"><Navigate to="/coach/identity" replace /></ProtectedRoute>} />
              <Route path="/coach/hub/lens" element={<ProtectedRoute role="coach"><Navigate to="/coach/hub/frameworks" replace /></ProtectedRoute>} />
              <Route path="/coach/hub/record" element={<ProtectedRoute role="coach"><CoachHubGuardrailsPage /></ProtectedRoute>} />
              <Route path="/coach/hub/alignment" element={<ProtectedRoute role="coach"><CoachAlignmentPage /></ProtectedRoute>} />
              <Route path="/coach/hub/veera" element={<ProtectedRoute role="coach"><Navigate to="/coach/hub" replace /></ProtectedRoute>} />
              <Route path="/coach/ai" element={<ProtectedRoute role="coach"><CoachAIPage /></ProtectedRoute>} />
              <Route path="/coach/veera" element={<ProtectedRoute role="coach"><CoachAIPage /></ProtectedRoute>} />
              <Route path="/coach/identity" element={<ProtectedRoute role="coach"><CoachIdentityPage /></ProtectedRoute>} />
              <Route path="/coach/identity/guided-form" element={<ProtectedRoute role="coach"><CoachIdentityGuidedFormPage /></ProtectedRoute>} />
              <Route path="/coach/messages" element={<ProtectedRoute role="coach"><CoachMessagesPage /></ProtectedRoute>} />
              <Route path="/coach/apps" element={<ProtectedRoute role="coach"><CoachAppsPage /></ProtectedRoute>} />
              <Route path="/coach/product-feedback" element={<ProtectedRoute role="coach"><ProductFeedbackPage /></ProtectedRoute>} />
              <Route path="/coach/ops/automation" element={<ProtectedRoute><AutomationOperatorAdminPage /></ProtectedRoute>} />
              <Route path="/coach/meetings" element={<ProtectedRoute role="coach"><CoachCallsPage /></ProtectedRoute>} />
              <Route path="/coach/meetings/:meetingId/reflect" element={<ProtectedRoute role="coach"><CoachReflectPage /></ProtectedRoute>} />
              <Route path="/coach/meetings/:meetingId" element={<ProtectedRoute role="coach"><CoachSessionEntryPage /></ProtectedRoute>} />
              <Route path="/coach/calendar" element={<ProtectedRoute role="coach"><RedirectWithSearch to="/coach/meetings" /></ProtectedRoute>} />
              <Route path="/coach/calls" element={<ProtectedRoute role="coach"><RedirectWithSearch to="/coach/meetings" /></ProtectedRoute>} />
              <Route path="/coach/reflect/:meetingId" element={<ProtectedRoute role="coach"><RedirectReflectToSession /></ProtectedRoute>} />
              <Route path="/coach/philosophy" element={<ProtectedRoute role="coach"><PhilosophyPage /></ProtectedRoute>} />
              <Route path="/coach/my-voice" element={<ProtectedRoute role="coach"><MyVoicePage /></ProtectedRoute>} />
              <Route path="/coach/settings" element={<ProtectedRoute role="coach" allowIncompleteOnboarding><CoachSettingsPage /></ProtectedRoute>} />
              <Route path="/coach/help" element={<ProtectedRoute role="coach"><CoachHelpPage /></ProtectedRoute>} />
              <Route path="/coach/review/:type/:leaderId" element={<ProtectedRoute role="coach"><CoachReviewPage /></ProtectedRoute>} />
              <Route path="/coach/review/sequence/:leaderId" element={<ProtectedRoute role="coach"><SequenceReviewPage /></ProtectedRoute>} />
              <Route path="/coach/post-meeting/:transcriptId" element={<ProtectedRoute role="coach"><CoachPostMeetingReviewPage /></ProtectedRoute>} />
              <Route path="/coach/onboarding" element={<ProtectedRoute role="coach" allowIncompleteOnboarding><CoachOnboardingFlow /></ProtectedRoute>} />

              {/* Coach legacy routes - redirect to new nav */}
              <Route path="/coach/dashboard" element={<Navigate to="/coach/overview" replace />} />
              <Route path="/coach/transcripts" element={<Navigate to="/coach/reviews" replace />} />
              <Route path="/coach/insights" element={<Navigate to="/coach/reviews" replace />} />
              <Route path="/coach/notifications" element={<Navigate to="/coach/overview" replace />} />

              {/* Leader Preview Routes — intentionally public (coach shares link with leader for demo).
                  PreviewLeaderProvider injects a fake session so page-level preview guards
                  short-circuit before any live Supabase reads or edge-function calls fire. */}
              <Route path="/preview/today" element={<PreviewLeaderProvider><TodayPage /></PreviewLeaderProvider>} />
              <Route path="/preview/schedule" element={<PreviewLeaderProvider><MomentsPage /></PreviewLeaderProvider>} />
              <Route path="/preview/insights" element={<PreviewLeaderProvider><InsightsPage /></PreviewLeaderProvider>} />
              <Route path="/preview/transcript-analysis/:transcriptId" element={<PreviewLeaderProvider><TranscriptAnalysisResultsPage /></PreviewLeaderProvider>} />
              <Route path="/preview/veera" element={<PreviewLeaderProvider><VeeraPage /></PreviewLeaderProvider>} />
              <Route path="/preview/coach" element={<PreviewLeaderProvider><LeaderMessagesPage /></PreviewLeaderProvider>} />
              <Route path="/preview/throughline" element={<PreviewLeaderProvider><LeaderHubPage /></PreviewLeaderProvider>} />
              <Route path="/preview/throughline/:section" element={<PreviewLeaderProvider><LeaderHubPage /></PreviewLeaderProvider>} />
              <Route path="/preview/post-meeting-capture" element={<PreviewPostMeetingCapturePage />} />
              <Route path="/preview/onboarding" element={<PreviewOnboardingPage />} />
              <Route path="/preview" element={<Navigate to="/preview/today" replace />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            </DemoModeProvider>
          </BrowserRouter>
          </TooltipProvider>
          </MyCoachContinuityBoundary>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
